import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiAlertCircle } = FiIcons;

/**
 * AuthCallback Component
 *
 * Handles auth redirects from Supabase (email confirmation, OAuth, etc.)
 * This page is shown when users click confirmation links in their emails.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Verifying your account...');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the current session after the redirect
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Auth callback error:', error);
          setStatus('error');
          setMessage(error.message || 'Failed to verify your account. Please try again.');
          return;
        }

        if (session) {
          setStatus('success');
          setMessage('Your account has been verified successfully!');

          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 2000);
        } else {
          // No session, might be a different type of callback
          // Check for hash params that might indicate password recovery
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const type = hashParams.get('type');

          if (type === 'recovery') {
            // Redirect to reset password page
            navigate('/auth/reset-password', { replace: true });
            return;
          }

          setStatus('success');
          setMessage('Email verified! You can now sign in.');

          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 2000);
        }
      } catch (err) {
        console.error('Unexpected error in auth callback:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00AAFF] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-400 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-md px-4"
      >
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 text-center">
          {status === 'processing' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 relative">
                <div className="w-20 h-20 border-4 border-[#00AAFF]/30 rounded-full"></div>
                <div className="w-20 h-20 border-4 border-[#00AAFF] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Verifying</h2>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-white" />
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-4">Success!</h2>
            </>
          )}

          {status === 'error' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <SafeIcon icon={FiAlertCircle} className="w-10 h-10 text-white" />
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-4">Error</h2>
            </>
          )}

          <p className="text-slate-300 mb-6">{message}</p>

          {status === 'success' && (
            <p className="text-slate-400 text-sm">Redirecting you...</p>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-[#00AAFF] to-[#0088dd] text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg hover:shadow-[#00AAFF]/50 transition-all"
              >
                Go to Login
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="w-full bg-white/5 border border-white/10 text-white py-3 px-4 rounded-xl font-medium hover:bg-white/10 transition-all"
              >
                Create Account
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AuthCallback;
